import { useMemoizedFn } from 'ahooks';
import isNumber from 'lodash/isNumber';
import { useRef } from 'react';
import type { IStaticInfo as IMatchInfo } from '../components/searchPdfToolBar/interface';
import type { IStaticInfo } from '../types';
import { usePdfCache } from './usePdfCache';
import { useSearchMatcher } from './useSearchMatcher';
import { useTextLayerManager } from './useTextLayerManager';

interface UsePDFSearchProps {
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js API
    pdfDoc: any;
    totalPages: number;
    scale: number;
    currentPage?: number;
    renderPixelRadio?: number;
}

/**
 * PDF 搜索 Hook（编排层）
 *
 * 将文字层管理委托给 useTextLayerManager，
 * 将搜索匹配委托给 useSearchMatcher。
 * 本层仅持有搜索词/选中态等搜索状态，并提供对外公共 API。
 */
export function usePdfSearch(props: UsePDFSearchProps) {
    const { pdfDoc, totalPages, scale, currentPage, renderPixelRadio } = props;

    // ── 搜索核心状态（本层持有） ──
    const searchTermRef = useRef<string>('');
    const allMatchesRef = useRef<IStaticInfo[]>([]);
    const activeMatchRef = useRef<null | { page: number; index: number }>(null);
    const searchCancelledRef = useRef(false);
    const currentSearchIdRef = useRef(0);

    // ── 页面缓存（共享给两个子 Hook） ──
    const { getCachedPage, getCachedTextContent } = usePdfCache(pdfDoc);

    const isValidPageNum = useMemoizedFn((pageNum: number) => pageNum >= 1 && pageNum <= totalPages);

    const isSearchCancelled = useMemoizedFn((searchId: number): boolean => {
        return searchCancelledRef.current || searchId !== currentSearchIdRef.current;
    });

    // ── 文字层管理 ──
    const {
        registerTextLayer,
        unregisterTextLayer,
        clearAllHighlights,
        highlightVisiblePages,
        collectVisiblePages,
        updateVisiblePages,
        renderCycleIdRef,
    } = useTextLayerManager({
        pdfDoc,
        totalPages,
        scale,
        currentPage,
        renderPixelRadio,
        getCachedPage,
        getCachedTextContent,
        searchTermRef,
        activeMatchRef,
    });

    // ── 搜索匹配 ──
    const { processChunksWithConcurrency } = useSearchMatcher({
        totalPages,
        getCachedTextContent,
        isSearchCancelled,
        onPageMatched: async (pageNum) => {
            const cycleId = renderCycleIdRef.current;
            await highlightVisiblePages([pageNum]);
            void cycleId; // satisfy linter – cycleId used via closure in highlightVisiblePages
        },
    });

    // ── 公共 API ──

    const onSearch = useMemoizedFn(async (value: string): Promise<IStaticInfo[]> => {
        searchCancelledRef.current = true;
        const searchId = ++currentSearchIdRef.current;
        searchCancelledRef.current = false;

        searchTermRef.current = value;
        activeMatchRef.current = null;
        const term = value.toLowerCase().trim();

        if (!term) {
            allMatchesRef.current = [];
            clearAllHighlights();
            return [];
        }
        if (!pdfDoc) return [];

        const CHUNK_SIZE = 5;
        const CONCURRENCY = 2;

        try {
            const chunks: Array<{ startPage: number; endPage: number }> = [];
            for (let i = 1; i <= totalPages; i += CHUNK_SIZE) {
                chunks.push({ startPage: i, endPage: Math.min(i + CHUNK_SIZE - 1, totalPages) });
            }

            const searchResult = await processChunksWithConcurrency({
                chunks,
                searchId,
                term,
                value,
                currentPage,
                concurrency: CONCURRENCY,
            });

            if (searchResult.cancelled) return [];

            allMatchesRef.current = searchResult.matches;
            const visiblePageList = collectVisiblePages();
            await highlightVisiblePages(visiblePageList);
            return searchResult.matches;
        } catch (_error) {
            return [];
        }
    });

    const clearSearch = useMemoizedFn(() => {
        searchTermRef.current = '';
        allMatchesRef.current = [];
        activeMatchRef.current = null;
        clearAllHighlights();
    });

    const setActiveMatch = useMemoizedFn((match: null | IMatchInfo) => {
        const prevMatch = activeMatchRef.current;
        if (!(match && isNumber(match.page) && isValidPageNum(match.page))) {
            activeMatchRef.current = null;
            if (prevMatch) {
                void highlightVisiblePages([prevMatch.page]);
            }
            return;
        }

        const idx = isNumber(match.index) ? match.index : 0;
        const nextMatch = { page: match.page, index: Math.max(0, idx) };

        if (prevMatch?.page === nextMatch.page && prevMatch?.index === nextMatch.index) return;

        activeMatchRef.current = nextMatch;
        if (prevMatch && prevMatch.page !== nextMatch.page) {
            void highlightVisiblePages([prevMatch.page]);
        }
        if (searchTermRef.current) {
            void highlightVisiblePages([nextMatch.page]);
        }
    });

    return {
        onSearch,
        registerTextLayer,
        unregisterTextLayer,
        clearSearch,
        updateVisiblePages,
        setActiveMatch,
    };
}
